import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import CodeEditor from './CodeEditor';
import ExitIcon from './exit-icon.png';

const maxSnippets = 20;

const ScorePage = () => {
	const navigate = useNavigate();
	const [snippet1, setSnippet1] = useState(null);
	const [snippet2, setSnippet2] = useState(null);
	const [comparison, setComparison] = useState(null);
	const [score, setScore] = useState(50);
	const [scoreCount, setScoreCount] = useState(1);
	const [completed, setCompleted] = useState(false);
	const [left, setLeft] = useState(false);
	const [language, setLanguage] = useState("java");

	const increaseCount = () => {
		setScoreCount(scoreCount + 1);
	
		if (scoreCount === maxSnippets) {
			setCompleted(true);
		}
	};

	const restartProcess = () => {
		setScoreCount(1);
		setCompleted(false);
	};

	const continueProcess = () => {
		setLeft(false);
	};

	const fetchSnippets = useCallback(async () => {
		try {
			const userInfo = localStorage.getItem("user");
			if (!userInfo) {
				navigate("/user-details");
				return;
			}
			const user = JSON.parse(userInfo);
			let query = "";
			if (user.languagesLevel1.length) {
				query += `?languages1=${user.languagesLevel1.join(",")}`;
			}
			if (user.languagesLevel2.length) {
				query += query ? `&languages2=${user.languagesLevel2.join(",")}` : `?languages2=${user.languagesLevel2.join(",")}`;
			}
			if (user.languagesLevel3.length) {
				query += query ? `&languages3=${user.languagesLevel3.join(",")}` : `?languages3=${user.languagesLevel3.join(",")}`;
			}
			const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/snippets${query}`);
			setSnippet1(response.data.snippet1);
			setSnippet2(response.data.snippet2);
			setScore(50);
			setComparison(response.data.comparison);
			setLanguage(response.data.language);
		} catch (error) {
			console.error('Error fetching snippets:', error);
		}
	}, [navigate]);

	useEffect(() => {
		fetchSnippets();
	}, [fetchSnippets]);

	const submitScore = async () => {
		try {
			const userInfo = localStorage.getItem("user");
			await axios.post(`${process.env.REACT_APP_SERVER_URL}/score`, {
				snippet1Id: snippet1._id,
				snippet2Id: snippet2._id,
				comparison,
				score: score/100,
				user: userInfo,
			});
			increaseCount();
			fetchSnippets();
		} catch (error) {
			console.error('Error submitting score:', error);
		}
	};

	const skipSnippets = () => {
		fetchSnippets();
	};

	const handleScoreChange = (event) => {
		setScore(event.target.value);
	};

	return (
		<div className="App">
			<div className="App-header">
				Functional Similarity Evaluation
			</div>
			{!completed && !left
			&& (
				<>
					<div style={{ display: "flex", marginTop: "20px", justifyContent: "center" }}>
						<span
							style={{
								color: "#00CBC4",
								fontSize: "1.3rem",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<span style={{ marginBottom: "5px" }}>Progress: {scoreCount} / 20</span>
							<progress id="progress-bar" value={scoreCount} max={20}></progress>
						</span>
						<button style={{ background: "transparent", outline: "none", border: "0px", cursor: "pointer", marginLeft: "10px" }} onClick={() => setLeft(true)}>
							<img aria-label="Exit process" src={ExitIcon} alt="exit" style={{ width: "30px", height: "30px" }} />
						</button>
					</div>
					{snippet1 && snippet2 && (
						<>
							<div className="Snippets-container">
								<div className="Snippet">
									<h2 style={{ color: "#DFEAF1", fontWeight: "bold" }}>Snippet 1</h2>
									<CodeEditor code={snippet1.code} language={language} />
								</div>
								<div className="Snippet">
									<h2 style={{ color: "#DFEAF1", fontWeight: "bold" }}>Snippet 2</h2>
									<CodeEditor code={snippet2.code} language={language} />
								</div>
							</div>
							<div className="Score-container">
								<div style={{ display: "flex", alignItems: "center", justifyContent: "space-evenly", width: "300px" }}>
									<div style={{ color: "white", fontSize: "1.2rem", marginRight: "2rem" }}>Similarity Score:</div>
									<div style={{ display: "flex", alignItems: "center" }}>
										<div style={{ color: "white", fontSize: "1.2rem" }}>{`${score}%`}</div>
									</div>
								</div>
								<input
									type="range"
									min="0"
									max="100"
									step="10"
									value={score}
									style={{ width: "200px", marginTop: "1rem", color: "white" }}
									onChange={handleScoreChange}
								/>
								<button
									className="submit-button"
									onClick={submitScore}
								>
									Submit
								</button>
								<button
									className="skip-button"
									onClick={skipSnippets}
								>
									Skip
								</button>
							</div>
						</>
					)}
				</>
			)}
			{completed && !left
			&& (
				<>
					<div className="description">
						<span style={{ marginTop: "50px", fontSize: "2rem" }}>{`The ${maxSnippets} snippets evaluation has been completed!`}</span>
						<br />
						<b style={{ color: "#00CBC4", fontSize: "2rem" }}>Thank you for your participation!</b>
					</div>
					<button
						className="submit-button"
						onClick={restartProcess}
					>
						Restart
					</button>
				</>
			)}
			{!completed && left
			&& (
				<>
					<div className="description">
						<span style={{ marginTop: "50px", fontSize: "2rem" }}>{`You can leave the evaluation at any time!`}</span>
						<span style={{ fontSize: "2rem" }}>{`Your progress has been saved!`}</span>
						<br />
						<b style={{ color: "#00CBC4", fontSize: "2rem" }}>Thank you for your participation!</b>
					</div>
					<button
						className="submit-button"
						onClick={continueProcess}
					>
						Continue
					</button>
				</>
			)}
		</div>
	);
};

export default ScorePage;
